import { useCallback } from 'react';
import { useImmerAtom } from 'jotai-immer';
import { ngDelegate } from '../../../../core/services/ng.delegate';
import { AllowedFileType, InputFileBlob } from '../../../../core/components/validation/ValidInputFile';
import utilSvc from '../../../../core/utilSvc';

// ira specific
import { iraImportStateAtom } from '../ira_list_atoms';
import { IraImportHook } from '../../ira_types';
import { iraImportStateVM } from '../ira_view_models';
import { useIraListStore } from '../pagehooks/useIraListStore';

// import
import { postImport } from '../../data/iraRepo';
import { HttpErrorUpsert, HttpImportResult } from '../../../../../global';
import validSvc from '../../../../core/validSvc';

export const useIraListImport = (): IraImportHook => {
    const pageStore = useIraListStore();
    const [state, setState] = useImmerAtom(iraImportStateAtom);

    const showImport = (): void => {
        setState(() => {
            const draft = iraImportStateVM();
            draft.showing = true;
            return draft;
        });
    };

    const cancel = useCallback((): void => {
        setState(() => iraImportStateVM());
        pageStore.getList();
    }, [setState, pageStore]);

    const setYear = (prop: string, val: string | number): void => {
        setState(draft => {
            draft.ContractYear.val = val.toString();
        });
    };

    const setFile = (prop: string, fileBlob: InputFileBlob | null): void => {
        setState(draft => {
            draft.ImportFile.file = fileBlob;
            draft.ImportFile.err = !!fileBlob ? '' : 'required';
        });
    };

    const modelValid = useCallback((): boolean => {
        let result = false;

        setState(draft => {
            draft.ContractYear.err = draft.ContractYear.val ? '' : 'required';

            // note below should be in a central validator once we get the kinks out

            // check binary file and size
            const file = draft.ImportFile.file;
            const validSize = draft.ImportFile.size;
            const fileIsValid = !!file && file.size > validSize.min && file.size < validSize.max;
            if (!fileIsValid) {
                draft.ImportFile.err = 'required with size min ' + validSize.min + ' max ' + validSize.max;
                return draft;
            }

            // check extension
            const allowedTypes = draft.ImportFile.types;
            const fileExt =
                validSvc.isText(file.name) && file.name.lastIndexOf('.') >= 0
                    ? file.name.substring(file.name.lastIndexOf('.'))
                    : '';
            const fileExtValid = allowedTypes.indexOf(fileExt as AllowedFileType) > -1;
            draft.ImportFile.err = fileExtValid ? '' : 'must be one of ' + allowedTypes.join(', ');

            result = draft.ContractYear.err === '' && draft.ImportFile.err === '';
            return draft;
        });

        return result;
    }, [setState]);

    const submitAsync = useCallback(async (): Promise<void> => {
        // API: IMPORT
        setState(draft => {
            draft.importing = true;
            draft.confirming = false;
        });
        const r = await postImport(state.ContractYear.val, state.ImportFile.file as Blob);
        setState(draft => {
            draft.importing = false;
            draft.confirming = false;
        });

        // ignore
        const err = r as HttpErrorUpsert;
        if (utilSvc.http.isIgnore(err)) {
            return;
        }

        let errMsg = '';
        switch (r.status) {
            case 200:
                setState(() => iraImportStateVM());
                pageStore.getList();
                const ok = r as HttpImportResult;
                ngDelegate.ok('Payors Imported', 'Received: ' + ok.Received + ', Processed: ' + ok.Processed);
                break;
            case 409:
                errMsg = 'File upload issue, try again.';
                if (!!err.timeout) {
                    errMsg += ' (timeout)';
                }
                ngDelegate.ok('Cannot Import', errMsg);
                break;
            case 422:
                errMsg = validSvc.http422ImportResultToString(r);
                ngDelegate.ok('Cannot Import', errMsg);
                break;
            default:
                ngDelegate.ok('Cannot Import', 'Unknown error occurred.');
        }
    }, [state, setState, pageStore]);

    const submit = useCallback(
        (confirmed?: boolean): boolean => {
            if (state.importing) {
                return false;
            }

            if (!modelValid()) {
                ngDelegate.ok('Cannot Import', 'Check properties');
                return false;
            }

            //tbd: user confirm?
            if (!confirmed) {
                setState(draft => {
                    draft.confirming = true;
                });
                return false;
            }

            submitAsync();
            return true;
        },
        [state, modelValid, setState, submitAsync],
    );

    return {
        state,
        showImport,
        setYear,
        setFile,
        submit,
        cancel,
    };
};
